import React, { useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "configs/store";
import { fetchUserCertificates, Certificate } from "slices/certificateSlice";
import { certificateApi } from "configs/api";
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Loader from "components/loader";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import { AppContext } from "context/appContext";

function Certificates(): React.JSX.Element {
  const dispatch = useDispatch();
  const { showAlert } = useContext(AppContext);
  const { certificates, loading } = useSelector((state: RootState) => state.certificates);
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const userId = user?._id || user?.id;
    if (userId) {
      dispatch(fetchUserCertificates(userId) as any);
    }
  }, [dispatch, user]);

  const handleDownloadPDF = async (pdfUrl?: string) => {
    try {
      if (!pdfUrl) {
        showAlert("PDF is not available for this certificate yet", "warning", "Unavailable");
        return;
      }

      const response = await certificateApi.get(pdfUrl, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);
      const disposition = response.headers?.["content-disposition"] as string | undefined;
      const fileNameMatch = disposition?.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
      const parsedFileName = fileNameMatch?.[1] ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, "")) : null;

      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", parsedFileName || "course-certificate.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      showAlert("Certificate downloaded successfully", "success", "Download");
    } catch (error: any) {
      showAlert(error.message || "Failed to download certificate", "error", "Error");
    }
  };

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" mb={1}>
          My Certificates
        </Typography>
        <Typography variant="body2" color="text">
          View and download your course completion certificates
        </Typography>
      </Box>

      {certificates.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" textAlign="center" color="text">
              No certificates yet. Complete courses and pass assessments to earn certificates.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={3}>
          {certificates.map((certificate: Certificate, index: number) => {
            const certificateId = certificate.id || certificate._id || `${certificate.certificateNumber}-${index}`;
            return (
            <Card key={certificateId}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={1}>
                  {certificate.courseName || "Course Completion Certificate"}
                </Typography>
                <Typography variant="body2" color="text" mb={1}>
                  Certificate #{certificate.certificateNumber}
                </Typography>
                <Typography variant="body2" color="text" mb={2}>
                  Issued: {new Date(certificate.issuedAt).toLocaleDateString()}
                </Typography>
                {certificate.completedAt && (
                  <Typography variant="body2" color="text" mb={2}>
                    Completed: {new Date(certificate.completedAt).toLocaleDateString()}
                  </Typography>
                )}
                {typeof certificate.completionProgress === "number" && (
                  <Typography variant="caption" color="text" display="block" mb={1}>
                    Completion Progress: {Math.round(certificate.completionProgress)}%
                  </Typography>
                )}
                <Box display="flex" flexDirection="column" gap={1} mt={2}>
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={() => handleDownloadPDF(certificate.pdfUrl)}
                    disabled={!certificate.pdfUrl}
                  >
                    Download PDF
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )})}
        </Box>
      )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default Certificates;

